/**
 * Central date utility — merges two company-level settings:
 *  - System Timezone (TenantSettings.timezone): dates render in the company's
 *    timezone instead of the browser's (pass it as the timeZone argument).
 *  - Date Format (TenantSettings.dateFormat): how the date part is written
 *    (DATE_FORMATS below: MM/DD/YYYY, MMM D, YYYY, DD.MM.YYYY, ...), chosen in Company
 *    Settings, cached here + localStorage so it applies from first paint.
 *  - Time Format (TenantSettings.timeFormat): 12-hour clock ("12:24:46 AM",
 *    the default, as Orders History shows it) or 24-hour clock ("00:24:46").
 * Super-admin sessions have no company profile and keep the defaults.
 */

export const DATE_FORMATS = [
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "MMM DD, YYYY",
  "MMM D, YYYY",
  "MMMM D, YYYY",
  "ddd, MMM D, YYYY",
  "DD MMM YYYY",
  "D MMMM YYYY",
  "MM-DD-YYYY",
  "DD-MM-YYYY",
  "DD.MM.YYYY",
  "YYYY/MM/DD",
] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];

const STORAGE_KEY = "companyDateFormat";

let current: DateFormat = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return DATE_FORMATS.includes(stored as DateFormat) ? (stored as DateFormat) : "MM/DD/YYYY";
  } catch {
    return "MM/DD/YYYY";
  }
})();

/** Call whenever the company profile loads or the setting is saved. */
export function setCompanyDateFormat(fmt?: string | null) {
  if (fmt && DATE_FORMATS.includes(fmt as DateFormat)) {
    current = fmt as DateFormat;
    try {
      localStorage.setItem(STORAGE_KEY, fmt);
    } catch {
      /* private mode — in-memory value still applies */
    }
  }
}

export function getCompanyDateFormat(): DateFormat {
  return current;
}

export const TIME_FORMATS = ["12h", "24h"] as const;
export type TimeFormat = (typeof TIME_FORMATS)[number];
export const TIME_FORMAT_LABELS: Record<TimeFormat, string> = {
  "12h": "12-hour — 1:45:30 PM",
  "24h": "24-hour — 13:45:30",
};

const TIME_STORAGE_KEY = "companyTimeFormat";

let currentTime: TimeFormat = (() => {
  try {
    const stored = localStorage.getItem(TIME_STORAGE_KEY);
    return TIME_FORMATS.includes(stored as TimeFormat) ? (stored as TimeFormat) : "12h";
  } catch {
    return "12h";
  }
})();

/** Call whenever the company profile loads or the setting is saved. */
export function setCompanyTimeFormat(fmt?: string | null) {
  if (fmt && TIME_FORMATS.includes(fmt as TimeFormat)) {
    currentTime = fmt as TimeFormat;
    try {
      localStorage.setItem(TIME_STORAGE_KEY, fmt);
    } catch {
      /* private mode — in-memory value still applies */
    }
  }
}

export function getCompanyTimeFormat(): TimeFormat {
  return currentTime;
}

/**
 * Company System Timezone (TenantSettings.timezone). Used whenever a caller
 * does not pass an explicit timeZone, so every date in the app renders in the
 * company's timezone (e.g. America/New_York) rather than the viewer's browser
 * timezone. Cached like the date format so it applies from first paint.
 */
const TZ_STORAGE_KEY = "companyTimezone";

let currentTz: string | undefined = (() => {
  try {
    return localStorage.getItem(TZ_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
})();

/** Call whenever the company profile loads or the setting is saved. */
export function setCompanyTimezone(tz?: string | null) {
  if (!tz || typeof tz !== "string" || !tz.trim()) return;
  currentTz = tz.trim();
  try {
    localStorage.setItem(TZ_STORAGE_KEY, currentTz);
  } catch {
    /* private mode — in-memory value still applies */
  }
}

export function getCompanyTimezone(): string | undefined {
  return currentTz;
}

const TIMEZONE_ALIASES: Record<string, string> = {
  EST: "America/New_York",
  EDT: "America/New_York",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  MST: "America/Denver",
  MDT: "America/Denver",
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
  IST: "Asia/Kolkata",
  GMT: "UTC",
  UTC: "UTC",
};

export const normalizeTimezone = (tz?: string): string | undefined => {
  if (!tz || typeof tz !== "string") return undefined;
  const trimmed = tz.trim();
  const upper = trimmed.toUpperCase();
  if (TIMEZONE_ALIASES[upper]) {
    return TIMEZONE_ALIASES[upper];
  }
  return trimmed;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PART_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

/**
 * One formatter per timezone, built once.
 *
 * Constructing an Intl.DateTimeFormat is one of the most expensive calls the
 * platform offers — it loads and parses locale data. This function runs once per
 * date cell, so a hundred-row grid built a hundred formatters per render, and
 * the grid re-renders on every keystroke in the search box. There are only ever
 * a couple of distinct timezones in play, so they are cached for the session.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

const formatterFor = (tz?: string): Intl.DateTimeFormat => {
  const key = tz ?? "";
  const hit = formatterCache.get(key);
  if (hit) return hit;
  const made = new Intl.DateTimeFormat(
    "en-US",
    tz ? { ...PART_OPTS, timeZone: tz } : PART_OPTS,
  );
  formatterCache.set(key, made);
  return made;
};

/** Y/M/D + time-of-day of the instant, evaluated in the target timezone (browser tz when omitted). */
function partsIn(d: Date, timeZone?: string): { y: string; m: string; day: string; hh: string; mm: string; ss: string } | null {
  const tz = normalizeTimezone(timeZone || currentTz);
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = formatterFor(tz).formatToParts(d);
  } catch (e) {
    console.warn(`[date.ts] Failed to format date with timezone "${timeZone}":`, e);
    // A bad timezone poisons the cache entry, so drop it before falling back.
    formatterCache.delete(tz ?? "");
    parts = formatterFor().formatToParts(d);
  }

  // One pass instead of six `parts.find()` scans over the same small array.
  const values: Record<string, string> = {};
  for (const p of parts) values[p.type] = p.value;
  const get = (type: string) => values[type] ?? "";
  const hh = get("hour");
  return {
    y: get("year"),
    m: get("month"),
    day: get("day"),
    // Intl with hour12:false can yield "24" for midnight in some engines.
    hh: hh === "24" ? "00" : hh,
    mm: get("minute"),
    ss: get("second"),
  };
}

const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Writes a calendar day in a format pattern: YYYY year, MMMM/MMM month name,
 * MM/M month number, DD/D day, ddd weekday. Everything else is copied as is.
 */
function applyDatePattern(p: { y: string; m: string; day: string }, pattern: string): string {
  const month = parseInt(p.m, 10);
  const dayNum = parseInt(p.day, 10);
  return pattern.replace(/YYYY|MMMM|MMM|MM|M|DD|D|ddd/g, (t) => {
    switch (t) {
      case "YYYY": return p.y;
      case "MMMM": return MONTHS_LONG[month - 1];
      case "MMM": return MONTHS[month - 1];
      case "MM": return p.m;
      case "M": return String(month);
      case "DD": return p.day;
      case "D": return String(dayNum);
      default: return WEEKDAYS[new Date(Date.UTC(+p.y, month - 1, dayNum)).getUTCDay()];
    }
  });
}

function dateStr(p: { y: string; m: string; day: string }): string {
  return applyDatePattern(p, current);
}

/** e.g. "MMM D, YYYY — Oct 1, 2026": a Date Format dropdown label with today as the example. */
export function dateFormatExample(fmt: DateFormat, sample: Date = new Date()): string {
  const p = partsIn(sample);
  return p ? applyDatePattern(p, fmt) : fmt;
}

function timeStr(
  p: { hh: string; mm: string; ss: string },
  hour12 = currentTime === "12h",
  seconds = true,
): string {
  const ss = seconds ? `:${p.ss}` : "";
  if (!hour12) return `${p.hh}:${p.mm}${ss}`;
  const h = parseInt(p.hh, 10);
  return `${h % 12 || 12}:${p.mm}${ss} ${h < 12 ? "AM" : "PM"}`;
}

/**
 * Date + time. Date part follows the company Date Format, time part the
 * company Time Format; both parts are
 * evaluated in the target timezone (company System Timezone when passed).
 * Custom Intl options bypass the company format (special layouts only).
 */
export const formatDateTime = (
  dateInput: any,
  timeZone?: string,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (dateInput === null || dateInput === undefined || dateInput === "") return "-";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "-";

  if (options) {
    const tz = normalizeTimezone(timeZone || currentTz);
    try {
      return new Intl.DateTimeFormat("en-US", tz ? { ...options, timeZone: tz } : options).format(d);
    } catch (e) {
      console.warn(`[date.ts] Failed to format date with timezone "${timeZone}":`, e);
      return d.toLocaleString("en-US", options);
    }
  }

  const p = partsIn(d, timeZone);
  if (!p) return "-";
  return `${dateStr(p)}, ${timeStr(p)}`;
};

/**
 * Date and time as separate strings, for the two-line layout Orders History
 * uses ("Oct 1, 2026" over "12:24:46 AM"). Null when the input isn't a date;
 * `time` is empty for a calendar day (see hasTimeOfDay).
 */
export const formatDateTimeParts = (
  dateInput: any,
  timeZone?: string,
  /** Off for dense grids, where to-the-second precision is noise. */
  { seconds = true }: { seconds?: boolean } = {},
): { date: string; time: string } | null => {
  if (dateInput === null || dateInput === undefined || dateInput === "") return null;
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return null;
  if (!hasTimeOfDay(dateInput)) {
    const day = partsIn(d, "UTC");
    return day ? { date: dateStr(day), time: "" } : null;
  }
  const p = partsIn(d, timeZone);
  if (!p) return null;
  return { date: dateStr(p), time: timeStr(p, undefined, seconds) };
};

/** Date only, in the company Date Format, evaluated in the target timezone. */
export const formatDateOnly = (dateInput: any, timeZone?: string): string => {
  if (dateInput === null || dateInput === undefined || dateInput === "") return "-";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "-";
  const p = partsIn(d, timeZone);
  if (!p) return "-";
  return dateStr(p);
};

/**
 * Whether a value carries a time of day. A plain calendar day - a "YYYY-MM-DD"
 * string, or a date stored as UTC midnight (PO dates, tag ranges, report days)
 * - has none, and is shown as a date only, read in UTC so it never slips to
 * the previous day west of Greenwich. Everything else is a real timestamp.
 */
export const hasTimeOfDay = (dateInput: any): boolean => {
  if (dateInput === null || dateInput === undefined || dateInput === "") return false;
  if (typeof dateInput === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInput.trim())) return false;
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return false;
  return !(
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  );
};

/**
 * The default for showing a date anywhere in the app: date and time together
 * when the value has a time, the date alone when it is a calendar day. Both
 * follow the company Date Format, Time Format and timezone.
 */
export const formatDate = (dateInput: any, timeZone?: string): string =>
  hasTimeOfDay(dateInput) ? formatDateTime(dateInput, timeZone) : formatDateOnly(dateInput, "UTC");

/**
 * Time part only, in the company Time Format (pass hour12 to force a clock),
 * evaluated in the target timezone.
 */
export const formatTimeOnly = (
  dateInput: any,
  timeZone?: string,
  hour12?: boolean,
): string => {
  if (dateInput === null || dateInput === undefined || dateInput === "") return "-";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "-";
  const p = partsIn(d, timeZone);
  if (!p) return "-";
  return hour12 === undefined ? timeStr(p) : timeStr(p, hour12);
};

/**
 * Calendar date of `at` in the company's time zone (the browser's when none is
 * set). Date filters send these, and the API reads them as the company's days,
 * so "Today" is the company's today: never the UTC date (`toISOString`), which
 * for a US company turns into tomorrow every evening.
 */
export function companyCalendar(at: Date = new Date()): { year: number; month: number; day: number } {
  const timeZone = normalizeTimezone(currentTz);
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(at);
  } catch {
    parts = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(at);
  }
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

const ymd = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/** "YYYY-MM-DD" in the company's time zone, `daysBack` days before today. */
export function companyDate(daysBack = 0, at: Date = new Date()): string {
  const { year, month, day } = companyCalendar(at);
  const d = new Date(Date.UTC(year, month - 1, day - daysBack));
  return ymd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** First day of the company's current month, or of its year (`yearsBack` years ago). */
export function companyMonthStart(at: Date = new Date()): string {
  const { year, month } = companyCalendar(at);
  return ymd(year, month, 1);
}
export function companyYearStart(yearsBack = 0, at: Date = new Date()): string {
  return ymd(companyCalendar(at).year - yearsBack, 1, 1);
}
